import { ButtonLooks, FontIcon, FontIcons, StandardButton } from '@brandfolder/react';
import { Trans, t } from '@lingui/macro';
import React, { FunctionComponent, useState } from 'react';

import { fetchJson } from '@api/ApiHelper';
import { Asset, AssetTypesPascalCaseSingular } from '@api/v4/assets/assetTypes';

import './styles/generative-ai-image-descriptions.scss';

interface GenerativeAiImageDescriptionsProps {
  asset: Asset;
  formattedDescription: string;
}
export const GenerativeAiImageDescriptions: FunctionComponent<GenerativeAiImageDescriptionsProps> = ({
  asset,
  formattedDescription,
}) => {
  const [assetDescription, setAssetDescription] = useState(formattedDescription);
  const [aiDescriptionLoading, setAiDescriptionLoading] = useState(false);
  const [aiDescriptionSaved, setAiDescriptionSaved] = useState(false);
  const [showSaveAiDescriptionButtons, setShowSaveAiDescriptionButtons] = useState(false);
  const updateAsset = async ({ assetKey, attributes }): Promise<void> => {
    try {
      await fetchJson({
        url: `/api/v4/assets/${assetKey}`,
        method: 'PUT',
        body: {
          data: {
            attributes
          }
        }
      });
      setAiDescriptionSaved(true);
      Notify.create({ type: 'success', title: t`Description saved` });
    } catch (error) {
      setAiDescriptionSaved(false);
    } finally {
      setShowSaveAiDescriptionButtons(false);
    }
  };

  const generateAiDescriptionRequest = async (): Promise<void> => {
    try {
      setAiDescriptionLoading(true);
      const aiDescriptionResponse = await fetch(
        'https://bf-stage.com/api/v1/data-insights/vertexai/blip_image_descriptions',
        {
          method: 'POST',
          mode: 'cors',
          // eslint-disable-next-line @typescript-eslint/object-curly-spacing, @typescript-eslint/naming-convention
          headers: { 'Content-Type': 'application/json', 'Accept': '*/*' },
          // eslint-disable-next-line @typescript-eslint/naming-convention
          body: JSON.stringify({ 'image_url': asset.display_image })
        }
      );

      if (aiDescriptionResponse.status !== 200) throw new Error();

      const responseText = await aiDescriptionResponse.text();
      const capitalizedDescription = responseText.charAt(0).toUpperCase() + responseText.slice(1);

      if (responseText.split('<')[1] === 'ai generated >') {
        setAssetDescription(t`${capitalizedDescription} *Generated by AI`);
      } else setAssetDescription(capitalizedDescription);

      setShowSaveAiDescriptionButtons(true);
    } catch (error) {
      Notify.create({ type: 'error', title: t`Description could not be generated` });
    } finally {
      setAiDescriptionLoading(false);
    }
  };

  const showGenerateAiDescriptionButton =
    !assetDescription &&
    !aiDescriptionLoading &&
    !showSaveAiDescriptionButtons &&
    !aiDescriptionSaved &&
    asset.type === AssetTypesPascalCaseSingular.GenericFile;

  const showDescriptionHeader =
    showGenerateAiDescriptionButton ||
    aiDescriptionLoading ||
    showSaveAiDescriptionButtons ||
    !!assetDescription;

  const isAiGenerated = assetDescription?.split('*')[1] === 'Generated by AI';

  const formattedAssetDescription = (): string => {
    const description = assetDescription?.split('*');
    if (isAiGenerated) {
      return description[0];
    } else return assetDescription;
  };

  return (
    <div className="generative-ai-feature-wrapper">
      <hr className="details-section-hr" />
      <div className="m-show-description s-asset-description">
        {showDescriptionHeader &&
          <span className="s-asset-details-title modal-label">
            <Trans>Description</Trans>
          </span>
        }
        {!showSaveAiDescriptionButtons && showDescriptionHeader &&
          <div className="generate-ai-description-wrapper">
            {aiDescriptionLoading &&
              <div className="generate-ai-description-loading">
                <FontIcon className="generate-ai-description-loading-icon" icon={FontIcons.SparkleFilled} />
                <span className="generate-ai-description-loading-text">
                  <Trans>Writing an AI description...</Trans>
                </span>
              </div>
            }
            {showGenerateAiDescriptionButton &&
              <StandardButton
                className="generate-ai-description-button"
                look={ButtonLooks.TextSecondary}
                onClick={(): void => { generateAiDescriptionRequest(); }}
                startIcon={FontIcons.SparkleFilled}
                type="button"
              >
                <Trans>Add an AI description</Trans>
              </StandardButton>
            }
          </div>
        }
        <div className="asset-description-text">
          {/* eslint-disable-next-line @typescript-eslint/naming-convention, react/no-danger */}
          <p dangerouslySetInnerHTML={{ __html: formattedAssetDescription() }} />
          {isAiGenerated && <span>&nbsp;[Generated by AI]</span>}
        </div>
        {showSaveAiDescriptionButtons && !aiDescriptionSaved && assetDescription &&
          <div className="generate-ai-action-buttons">
            <StandardButton
              className="generate-ai-submit-button"
              look={ButtonLooks.Primary}
              onClick={(): void => {
                updateAsset({
                  assetKey: asset.asset_key,
                  attributes: { description: assetDescription }
                });
              }}
            >
              <Trans>Save</Trans>
            </StandardButton>
            <StandardButton
              className="generate-ai-cancel-button"
              look={ButtonLooks.Tertiary}
              onClick={(): void => {
                setAssetDescription(formattedDescription);
                setShowSaveAiDescriptionButtons(false);
              }}
            >
              <Trans>Cancel</Trans>
            </StandardButton>
          </div>
        }
      </div>
    </div>
  );
};
